import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MaterialTable, { MTableToolbar } from 'material-table';

import { useTranslation } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';

// icon
import { faImage, faVideo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// style
import styles from '../style';
import { StyledIconButton } from '../../../../components/button/Button';
import tableIcons from '../../../../components/table/MaterialTableIcon';

// Utils
import exportCsv from '../../../../utils/export/Csv';

import CCTVModal from '../../../../components/modal/Modal';
import CctvMedia from './CctvMedia';

const useStyles = makeStyles(styles);

export default function WLCctvDataLists(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    datas, handleSearchMarker, handleSetCurrentData, currentMode, currentArea, userIsLogin,
  } = props;
  const unmounted = useRef(false);
  const [modalData, setModalData] = useState({
    open: false,
    header: 'CCTV',
    subheader: null,
    content: null,
  });

  const isExport = (userIsLogin) ? { csv: true } : userIsLogin;

  // force modal close
  useEffect(() => {
    if (modalData.open) modalData.open = false;
    return () => { unmounted.current = true; };
  }, [modalData.open]);

  function filterData(currentData, mode, area) {
    let filterD = [];

    if (mode === 'province' && area.code !== '0') {
      filterD = datas.filter((d) => d.geocode.province_code === area.code);
    } else if (currentMode === 'basin' && area.code !== '0') {
      filterD = datas.filter((d) => d.basin.basin_code === parseInt(area.code, 10));
    } else {
      filterD = currentData;
    }

    handleSetCurrentData(filterD);
    return filterD;
  }

  const handleModal = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: 'CCTV',
      subheader: null,
      content: <CctvMedia data={data} />,
    });
  };

  const showData = () => (
    <MaterialTable
      icons={tableIcons}
      columns={[
        {
          title: t('ชื่อสถานี'),
          title_export: t('ชื่อสถานี'),
          field: 'title',
          width: 190,
          headerStyle: {
            left: 0,
            position: 'sticky',
            zIndex: 50,
          },
          cellStyle: {
            left: 0,
            position: 'sticky',
            wordWrap: 'normal',
            padding: '0em',
          },
          render: (row) => (
            <Box display="flex" style={{ backgroundColor: 'white' }}>
              <Button onClick={handleSearchMarker(row.id, row.lat, row.long)} title="แสดงตำแหน่งบนแผนที่">
                {`${row.title}`}
              </Button>
            </Box>
          ),
          customSort: (a, b) => a.title.localeCompare(b.title, 'th'),
        },
        {
          title: t('รหัสสถานี'),
          type: 'string',
          field: 'dam_id',
          hidden: true,
          export: false,
          searchable: true,
        },
        {
          title_export: t('สถานี'),
          type: 'string',
          field: 'title',
          hidden: true,
        },
        {
          title_export: t('ตำบล'),
          type: 'string',
          field: 'geocode.tumbon_name.th',
          searchable: true,
          export: false,
          hidden: true,
        },
        {
          title_export: t('อำเภอ'),
          type: 'string',
          field: 'geocode.amphoe_name.th',
          searchable: true,
          export: false,
          hidden: true,
        },
        {
          title: t('ที่ตั้ง'),
          title_export: t('จังหวัด'),
          field: 'geocode.province_name.th',
          type: 'string',
          width: 350,
          render: (row) => `${t('ต.')}${row.geocode.tumbon_name.th} ${t('อ.')}${row.geocode.amphoe_name.th} ${t('จ.')}${row.geocode.province_name.th}`,
        },
        {
          title: t('ลุ่มน้ำ'),
          title_export: t('ลุ่มน้ำ'),
          type: 'string',
          field: 'basin.basin_name.th',
          width: 200,
          hidden: !userIsLogin,
        },
        {
          title_export: 'Latitude',
          type: 'string',
          field: 'lat',
          hidden: true,
        },
        {
          title_export: 'Longitude',
          type: 'string',
          field: 'long',
          hidden: true,
        },
        {
          title: t('ภาพ'),
          field: '',
          sorting: false,
          width: 115,
          render: (row) => {
            let iconStyle = faImage;
            if (row.media_type === 'vdo') {
              iconStyle = faVideo;
            }
            return (
              <>
                <Tooltip title={t('แสดงข้อมูล CCTV')}>
                  <StyledIconButton onClick={handleModal(row)}>
                    <FontAwesomeIcon icon={iconStyle} style={{ color: '#888' }} />
                  </StyledIconButton>
                </Tooltip>
              </>
            );
          },
        },

      ]}
      data={filterData(datas, currentMode, currentArea)}
      localization={{
        toolbar: {
          searchPlaceholder: t('ค้นหา'),
          exportTitle: t('ดาวน์โหลดข้อมูล CCTV'),
        },
        pagination: {
          labelRowsSelect: t('แถว'),
        },
        body: { emptyDataSourceMessage: datas.length === 0 ? <h3>{t('กำลังโหลดข้อมูล...')}</h3> : <h3>{t('ไม่มีข้อมูลตามเงื่อนไข')}</h3> },
      }}
      options={{
        // tableLayout: 'fixed',
        pageSize: 10,
        sorting: true,
        // https://stackoverflow.com/questions/61048405/material-ui-how-to-make-the-search-textfield-expandable-in-material-table
        // searchFieldStyle: {
        //   width: '50%',
        //   alignSelf: { sm: 'center', md:'flex-end' },
        // },
        rowStyle: {
          wordWrap: 'break-word',
        },
        toolbar: true,
        showTitle: false,
        search: true,
        exportButton: isExport,
        exportCsv,
        cellStyle: {
          padding: 8,
        },
      }}
      components={{
        Toolbar: (props) => (
          <Box mx={2}>
            <Grid container direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
              <Grid item xs={12} sm={8} md={6}>
                <MTableToolbar {...props} />
              </Grid>
            </Grid>
          </Box>
        ),
      }}
    />
  );

  const Modal = React.memo((p) => (
    <CCTVModal
      open={p.modalData.open}
      modalHeader={p.modalData.header}
      modalSubHeader={p.modalData.subheader}
      modalContent={p.modalData.content}
    />
  ));

  return (
    <>
      <Paper className={classes.root}>
        {showData()}

        <Modal modalData={modalData} />
      </Paper>

    </>
  );
}

WLCctvDataLists.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  userIsLogin: PropTypes.bool.isRequired,
  handleSearchMarker: PropTypes.func.isRequired,
  handleSetCurrentData: PropTypes.func.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
};
